import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { RtkDropzone } from 'src/components/RtkDropzone'
import { useTemplateDatabase } from 'src/redux/services/ubem'
import {
  getOrgTemplateLibraries,
  getProject
} from 'src/redux/slices/project/endpoints'
import {
  mutationFixedCacheKeys,
  uploadUserTemplateLibUmibuilder
} from 'src/redux/slices/umibuilder/endpoints'
import {
  selectTemplateLibraryFilters,
  selectUmiBuilderFromCloud
} from 'src/redux/slices/umibuilder/selectors'

import Grid from '@material-ui/core/Grid'
import {
  DataGrid,
  GridColDef,
  GridColumns,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarDensitySelector
} from '@mui/x-data-grid'

import { TemplateTableProps } from './AssignTemplateDialog'
import CloudTemplateLibSelector from './CloudTemplateLibSelector'
import TemplateTableFilter, { TemplateFilter } from './TemplateTableFilter'

const dropzoneProps = {
  label: 'User Template Library',
  showBrowseButton: true,
  mutation: uploadUserTemplateLibUmibuilder.useMutation,
  index: mutationFixedCacheKeys.uploadUserTemplateLib,
  skipReset: true,
  maxFiles: 1,
  accept: 'application/json, .json',
}

const columns: GridColumns = [
  {
    field: 'Name',
    headerName: 'Name',
    minWidth: 250,
    flex: 1,
    editable: false,
  },
  {
    field: 'ClimateZone',
    headerName: 'Climate Zone',
    minWidth: 90,
    flex: 1,
    editable: false,
    type: 'string',
    valueFormatter: ({ value }) => (value as string[] | undefined)?.join(', ') ?? '',
  },
  {
    field: 'Type',
    headerName: 'Type',
    minWidth: 90,
    flex: 1,
    editable: false,
    valueFormatter: ({ value }) => (value as string[] | undefined)?.join(', ') ?? '',
  },
  {
    field: 'Country',
    headerName: 'Country',
    minWidth: 90,
    flex: 1,
    editable: false,
  },
  {
    field: 'YearFrom',
    headerName: 'Year From',
    minWidth: 90,
    flex: 1,
    hide: true,
    editable: false,
  },
  {
    field: 'YearTo',
    headerName: 'Year To',
    minWidth: 90,
    flex: 1,
    hide: true,
    editable: false,
  },
  {
    field: 'DataSource',
    headerName: 'Data Source',
    minWidth: 90,
    flex: 1,
    hide: true,
    editable: false,
  },
  {
    field: 'Comments',
    headerName: 'Comments (hover)',
    minWidth: 90,
    flex: 1,
    hide: true,
    editable: false,
  },
]

const CustomToolbar = () => {
  return (
    <GridToolbarContainer>
      <GridToolbarDensitySelector />
      <GridToolbarColumnsButton />
    </GridToolbarContainer>
  )
}

const TemplateTable: React.FC<TemplateTableProps> = ({ assign }) => {
  const { data: databaseTemplateData } = useTemplateDatabase.useQuery('')
  const fromCloud = useSelector(selectUmiBuilderFromCloud)
  const [_, { data: userTemplateData }] = uploadUserTemplateLibUmibuilder.useMutation({
    fixedCacheKey: mutationFixedCacheKeys.uploadUserTemplateLib,
  })

  const data = useMemo(() => {
    const databaseTemplates = databaseTemplateData ? databaseTemplateData : []
    const userTemplates = userTemplateData ? userTemplateData : []
    return [...userTemplates, ...databaseTemplates]
  }, [userTemplateData, databaseTemplateData])

  const [pageSize, setPageSize] = useState(10)

  const filters = useSelector(selectTemplateLibraryFilters)

  const filteredData = useMemo(() => {
    if (!data) return []
    return data.filter(({ ClimateZone, Type }) => {
      let pass = true
      if (filters.ClimateZone.length > 0) {
        if (filters.ClimateZone.filter((zone) => ClimateZone.includes(zone)).length === 0)
          pass = false
      }
      if (filters.Type.length > 0) {
        if (filters.Type.filter((t) => Type?.includes(t)).length === 0) pass = false
      }
      return pass
    })
  }, [filters.Type, filters.ClimateZone, data])

  return (
    <>
      <Grid container spacing={2} justifyContent='center' alignItems='center'>
        <Grid item xs>
          <TemplateTableFilter parameter={TemplateFilter.ClimateZone} />
        </Grid>
        <Grid item xs>
          <TemplateTableFilter parameter={TemplateFilter.Type} />
        </Grid>
        <Grid item xs={12}>
          <div style={{ display: 'flex' }}>
            <div style={{ flexGrow: 1 }}>
              <DataGrid
                autoHeight
                rows={filteredData}
                columns={columns}
                pageSize={pageSize}
                onPageSizeChange={(size) => setPageSize(size)}
                rowsPerPageOptions={[5, 10, 25, 50, 100]}
                onRowClick={assign}
                sortModel={[
                  { field: 'ClimateZone', sort: 'asc' },
                  { field: 'Type', sort: 'asc' },
                ]}
                components={{
                  Toolbar: CustomToolbar,
                }}
                density='compact'
              />
            </div>
          </div>
        </Grid>
        <Grid item xs={6}>
          {!fromCloud && <RtkDropzone {...dropzoneProps} />}
          {fromCloud && <CloudTemplateLibSelector />}
        </Grid>
      </Grid>
    </>
  )
}

export default TemplateTable
